import React, { useState, useEffect, useRef } from 'react';
import HomePage from './components/HomePage';
import ComptableDashboard from './components/ComptableDashboard';
import DashboardConfigurator from './components/DashboardConfigurator';
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/AdminDashboard';
import ClientLogin from './components/ClientLogin';
import PricingPage from './components/PricingPage';
import MobileUpload from './components/MobileUpload';
import KbisUploadModal from './components/KbisUploadModal';
import Confidentialite from './components/Confidentialite';
import api, { getAuthToken, removeAuthToken } from './services/api';
import { track } from './services/analytics';

function App() {
  const [view, setView] = useState('home'); // 'home' | 'dashboard' | 'configurator' | 'admin-login' | 'admin-dashboard' | 'client-login' | 'pricing'
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
  const [clientInfo, setClientInfo] = useState(null); // { id, nom, token, is_projet }
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [loginTab, setLoginTab] = useState('login'); // 'login' | 'register'
  const [isKbisModalOpen, setIsKbisModalOpen] = useState(false);
  const inactivityLogoutRef = useRef(false);

  // Lien de réinitialisation : https://compta-ia-app.fr/?reset=<token>
  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get('reset');
    if (token) {
      setResetToken(token);
      setRecoveryMode(true);
      setView('client-login');
    }
  }, []);

  // Valider la session stockée auprès du backend (JWT local, plus de Supabase).
  // On ne fait plus confiance au seul localStorage : un token expiré est purgé.
  useEffect(() => {
    const existingToken = getAuthToken();
    const savedClientId = localStorage.getItem('auth_client_id');

    if (!existingToken || !savedClientId) return undefined;

    let cancelled = false;
    (async () => {
      const res = await api.get('/auth/me');
      if (cancelled) return;
      if (res && res.success && res.role === 'client' && res.client) {
        setClientInfo({ id: res.client.id, nom: res.client.nom });
        localStorage.setItem('auth_client_id', res.client.id);
        localStorage.setItem('auth_client_nom', res.client.nom);
        setView('dashboard');
      } else {
        removeAuthToken();
        localStorage.removeItem('auth_client_id');
        localStorage.removeItem('auth_client_nom');
        setClientInfo(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const isDashboard = view === 'dashboard' || view === 'admin-dashboard' || view === 'configurator';

  // Pose/retire la classe 'landing-page' sur body pour que le CSS mobile
  // ne verrouille pas le scroll sur les pages publiques (home, login, pricing…)
  useEffect(() => {
    const body = document.body;
    if (isDashboard) {
      body.classList.remove('landing-page');
    } else {
      body.classList.add('landing-page');
    }
    return () => {
      body.classList.remove('landing-page');
    };
  }, [isDashboard]);

  // Le dashboard gère son propre contenu défilable : ne pas verrouiller le document.
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');

    if (!isDashboard) {
      html.style.overflow = '';
      html.style.position = '';
      html.style.width = '';
      html.style.height = '';
      html.style.overscrollBehaviorY = '';

      body.style.overflow = '';
      body.style.position = '';
      body.style.width = '';
      body.style.height = '';
      body.style.overscrollBehaviorY = '';

      if (root) {
        root.style.height = '';
        root.style.overflow = '';
      }
    }
  }, [isDashboard]);

  // Déconnecter un client après 30 minutes sans interaction locale.
  useEffect(() => {
    if (view !== 'dashboard') return undefined;

    const inactivityLimitMs = 30 * 60 * 1000;
    let inactivityTimer;

    const signOutInactiveClient = async () => {
      inactivityLogoutRef.current = true;
      removeAuthToken();
      localStorage.removeItem('auth_client_id');
      localStorage.removeItem('auth_client_nom');
      sessionStorage.clear();
      setClientInfo(null);
      setView('client-login');
    };

    const resetInactivityTimer = () => {
      window.clearTimeout(inactivityTimer);
      inactivityTimer = window.setTimeout(signOutInactiveClient, inactivityLimitMs);
    };

    const activityEvents = ['click', 'keydown', 'scroll', 'touchstart'];
    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, resetInactivityTimer, { passive: true });
    });
    resetInactivityTimer();

    return () => {
      window.clearTimeout(inactivityTimer);
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, resetInactivityTimer);
      });
    };
  }, [view]);

  // Router basique basé sur l'état
  const renderView = () => {
    if (window.location.pathname === '/mobile') {
      return <MobileUpload />;
    }
    console.log('DEBUG: view actuelle =', view, 'clientInfo =', clientInfo);

    if (view === 'home') {
      return (
        <HomePage
          onGoToLogin={() => {
            setLoginTab('login');
            setView('client-login');
          }}
          onGoToRegister={() => {
            setLoginTab('register');
            setView('client-login');
          }}
          onGoToAdmin={() => setView('admin-login')}
          onGoToPricing={() => setView('pricing')}
          onGoToConfidentialite={() => setView('confidentialite')}
        />
      );
    } else if (view === 'confidentialite') {
      return <Confidentialite onBack={() => setView('home')} />;
    } else if (view === 'pricing') {
      return (
        <PricingPage
          onBack={() => setView('home')}
          onChoosePlan={() => {
            setLoginTab('register');
            setView('client-login');
          }}
        />
      );
    } else if (view === 'client-login') {
      console.log('DEBUG: affichage page de connexion client');
      return (
        <ClientLogin
          recoveryMode={recoveryMode}
          resetToken={resetToken}
          initialTab={loginTab}
          onLoginSuccess={(clientId, clientNom) => {
            console.log('DEBUG: login success, passage à dashboard pour', clientId);
            setRecoveryMode(false);
            setClientInfo({ id: clientId, nom: clientNom });
            if (clientId) localStorage.setItem('auth_client_id', clientId);
            if (clientNom) localStorage.setItem('auth_client_nom', clientNom);
            track('login', 'login_client');
            setView('dashboard');
          }}
          onCancelRecovery={() => {
            setRecoveryMode(false);
            setView('home');
          }}
        />
      );
    } else if (view === 'admin-login') {
      return <AdminLogin onLoginSuccess={() => { track('login', 'login_admin'); setIsAdminLoggedIn(true); setView('admin-dashboard'); }} />;
    } else if (view === 'admin-dashboard') {
      return <AdminDashboard onLogout={() => { setIsAdminLoggedIn(false); setView('home'); }} />;
    } else if (view === 'configurator') {
      return <DashboardConfigurator onBack={() => setView('dashboard')} />;
    } else if (view === 'dashboard') {
      console.log('DEBUG: affichage dashboard pour client', clientInfo?.id);
      const isProjet = clientInfo?.id?.startsWith('projet_');
      return (
        <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {isProjet && (
            <div style={{
              background: 'linear-gradient(90deg, #1D9E75 0%, #0F6E56 100%)',
              color: 'white', padding: '10px 20px', display: 'flex',
              alignItems: 'center', justifyContent: 'space-between', fontSize: '14px',
              fontWeight: '500', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', zIndex: 100
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>💡</span>
                <span>Mode Projet Créateur ({clientInfo?.nom}) — En cours d'immatriculation</span>
              </div>
              <button
                onClick={() => setIsKbisModalOpen(true)}
                style={{
                  background: '#FFFFFF', color: '#0F6E56', border: 'none',
                  padding: '6px 14px', borderRadius: '6px', fontSize: '13px',
                  fontWeight: '700', cursor: 'pointer', transition: 'transform 0.2s'
                }}
              >
                📄 J'ai reçu mon K-Bis : Finaliser mon compte
              </button>
            </div>
          )}
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <ComptableDashboard
              clientId={clientInfo?.id}
              clientNom={clientInfo?.nom}
              onLogout={() => {
                removeAuthToken();
                localStorage.removeItem('auth_client_id');
                localStorage.removeItem('auth_client_nom');
                setClientInfo(null);
                setView('home');
              }}
            />
          </div>
        </div>
      );
    }
  };

  const containerStyle = isDashboard
    ? { height: '100vh', overflow: 'hidden' }
    : { minHeight: '100vh', overflowY: 'auto' };

  const [isOffline, setIsOffline] = useState(false);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    // Ne marquer hors-ligne que si l'événement offline est effectivement déclenché
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      // Double check avec un ping réseau passif
      fetch('/manifest.json', { method: 'HEAD', cache: 'no-store' })
        .then(() => setIsOffline(false))
        .catch(() => setIsOffline(true));
    }

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <div className="app-container" style={containerStyle}>
      {isOffline && (
        <div style={{
          background: '#FEF2F2',
          color: '#991B1B',
          borderBottom: '1px solid #FCA5A5',
          padding: '8px 16px',
          textAlign: 'center',
          fontSize: '13px',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          position: 'sticky',
          top: 0,
          zIndex: 10000
        }}>
          <span>🌐</span> Vous êtes actuellement hors-ligne. L'application continue de fonctionner en mode consultation PWA.
        </div>
      )}

      {renderView()}

      <KbisUploadModal
        isOpen={isKbisModalOpen}
        onClose={() => setIsKbisModalOpen(false)}
        onUpgradeSuccess={(newClient) => {
          setIsKbisModalOpen(false);
          setClientInfo({ id: newClient.id, nom: newClient.nom });
          localStorage.setItem('auth_client_id', newClient.id);
          localStorage.setItem('auth_client_nom', newClient.nom);
          setView('dashboard');
        }}
      />
    </div>
  );
}

export default App;
